import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api/client.js';

export type UserPreferences = {
  theme: 'light' | 'dark' | 'system';
  defaultTicketView: 'list' | 'cards' | 'kanban';
  defaultQueueFilter: 'all_active' | 'my_tickets' | 'unassigned';
  defaultReportPeriod: 'today' | 'week' | 'month' | 'quarter' | 'year';
  defaultIntakeType: 'walk_in' | 'drop_off' | 'mail_in';
  autoAssignToMe: boolean;
  promisedTimeOffset: 'same_day' | 'next_day' | 'calculator';
  language: string;
  ticketDetailView?: 'tech' | 'full';
};

export type NotificationPrefs = {
  inApp: Record<string, boolean>;
  email: Record<string, boolean>;
  sms: Record<string, boolean>;
  emailOverride: string | null;
  smsOverride: string | null;
};

export type MyProfile = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  avatarUrl: string | null;
  role: string;
  storeId: string;
  storeName: string | null;
  storeIds: string[] | null;
  tenantId: string | null;
  pinSet: boolean;
  pinSetAt: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  preferences: UserPreferences;
  notificationPrefs: NotificationPrefs;
};

export function fetchMyProfile() {
  return apiGet<MyProfile>('/api/v1/users/me').then((r) => r.data);
}

export function updateMyProfile(body: {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string | null;
  avatarUrl?: string | null;
}) {
  return apiPatch<
    Pick<MyProfile, 'id' | 'firstName' | 'lastName' | 'email' | 'phone' | 'avatarUrl' | 'role'>,
    typeof body
  >('/api/v1/users/me', body).then((r) => r.data);
}

export function changeMyPassword(body: { currentPassword: string; newPassword: string }) {
  return apiPost<{ success: boolean; requireReLogin: boolean }, typeof body>(
    '/api/v1/users/me/change-password',
    body,
  ).then((r) => r.data);
}

export function updateMyPreferences(body: Partial<UserPreferences>) {
  return apiPatch<UserPreferences, Partial<UserPreferences>>(
    '/api/v1/users/me/preferences',
    body,
  ).then((r) => r.data);
}

export function updateMyNotificationPrefs(body: Partial<NotificationPrefs>) {
  return apiPatch<NotificationPrefs, Partial<NotificationPrefs>>(
    '/api/v1/users/me/notification-prefs',
    body,
  ).then((r) => r.data);
}

export function signOutOtherSessions() {
  return apiPost<{ success: boolean; revoked: number }>('/api/v1/users/me/sign-out-others').then(
    (r) => r.data,
  );
}

// Re-export pin helpers used on profile page
export { getPinStatus, setPin, clearPin as deletePin } from '@/lib/api/pin.js';
